import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SILENT } from '@core';
import { Observable, forkJoin, map } from 'rxjs';

import { PromptCard, PromptSection } from '../models/prompt.model';
import { AiAssistantService } from './ai-assistant.service';

interface ApiResponse<T> {
  data?: T;
}

export interface PromptLibraryData {
  sections: PromptSection[];
  prompts: PromptCard[];
}

export interface PromptSuggestionCard {
  id: number;
  workflow: string;
  prompt: string;
  constraints?: unknown[];
  outputSpec?: string;
  status?: number;
}

export interface SavePromptCardAnalysisSettingsResponse {
  promptCardId: number;
  analysisSettings?: string;
  savedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PromptLibraryService {
  constructor(
    private http: HttpClient,
    private aiService: AiAssistantService
  ) {}

  getPromptLibrary(): Observable<PromptLibraryData> {
    return forkJoin({
      sections: this.getSections(),
      prompts: this.getPrompts()
    });
  }

  getPromptSuggestions(): Observable<PromptSuggestionCard[]> {
    return this.http
      .get<ApiResponse<{ cards?: PromptSuggestionCard[] }>>(
        `${this.aiService.aiServerUrl}/api/PromptSuggestions`
      )
      .pipe(map(response => this.unwrap(response)?.cards ?? []));
  }

  togglePin(promptId: number): Observable<boolean> {
    return this.http
      .post<ApiResponse<{ isPinned: boolean }>>(
        `${this.aiService.aiServerUrl}/api/PromptCards/${promptId}/TogglePin`,
        {},
        { context: new HttpContext().set(SILENT, true) }
      )
      .pipe(map(response => this.unwrap(response)?.isPinned ?? false));
  }

  saveAnalysisSettings(
    promptId: number,
    analysisSettings: string | null
  ): Observable<SavePromptCardAnalysisSettingsResponse> {
    return this.http
      .post<ApiResponse<SavePromptCardAnalysisSettingsResponse>>(
        `${this.aiService.aiServerUrl}/api/PromptCards/${promptId}/SaveAnalysisSettings`,
        {
          analysisSettings
        },
        { context: new HttpContext().set(SILENT, true) }
      )
      .pipe(
        map(
          response =>
            this.unwrap(response) ?? {
              promptCardId: promptId,
              analysisSettings: analysisSettings ?? undefined
            }
        )
      );
  }

  getSections(): Observable<PromptSection[]> {
    return this.http
      .get<
        ApiResponse<PromptSection[]>
      >(`${this.aiService.aiServerUrl}/api/PromptSections`)
      .pipe(map(response => this.unwrap(response) ?? []));
  }

  getPrompts(sectionId?: number): Observable<PromptCard[]> {
    let params = new HttpParams();
    if (sectionId) {
      params = params.set('sectionId', sectionId.toString());
    }

    return this.http
      .get<
        ApiResponse<PromptCard[]>
      >(`${this.aiService.aiServerUrl}/api/PromptCards`, { params })
      .pipe(map(response => this.unwrap(response) ?? []));
  }

  private unwrap<T>(response: ApiResponse<T> | T): T {
    return (response as ApiResponse<T>)?.data ?? (response as T);
  }
}
